/**
 * Copyright 2015 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __extends } from "tslib";
import { alCoerceNumber, alDefineObjectProperties, alToNumber } from '../runtime';
import { Point } from '@awayjs/core';
import { AVM1Object } from '../runtime/AVM1Object';
import { AVM1Function } from '../runtime/AVM1Function';
export function toAS3Point(v) {
    if (!v) {
        return new Point(0, 0);
    }
    var context = v.context;
    var x, y;
    if (v instanceof AVM1Object) {
        x = alCoerceNumber(context, v.alGet('x'));
        y = alCoerceNumber(context, v.alGet('y'));
    }
    return new Point(x, y);
}
export function copyAS3PointTo(p, v) {
    v.alPut('x', p.x);
    v.alPut('y', p.y);
}
var AVM1Point = /** @class */ (function (_super) {
    __extends(AVM1Point, _super);
    function AVM1Point(context, x, y) {
        var _this = _super.call(this, context) || this;
        _this.alPrototype = context.globals.Point.alGetPrototypeProperty();
        _this.alPut('x', x);
        _this.alPut('y', y);
        return _this;
    }
    AVM1Point.fromAS3Point = function (context, p) {
        return new AVM1Point(context, p.x, p.y);
    };
    return AVM1Point;
}(AVM1Object));
export { AVM1Point };
var AVM1PointFunction = /** @class */ (function (_super) {
    __extends(AVM1PointFunction, _super);
    function AVM1PointFunction(context) {
        var _this = _super.call(this, context) || this;
        alDefineObjectProperties(_this, {
            prototype: {
                value: new AVM1PointPrototype(context, _this)
            },
            distance: {
                value: _this.distance,
                writable: true
            },
            interpolate: {
                value: _this.interpolate,
                writable: true
            },
            polar: {
                value: _this.polar,
                writable: true
            }
        });
        return _this;
    }
    AVM1PointFunction.prototype.alCall = function (thisArg, args) {
        return this.alConstruct(args);
    };
    AVM1PointFunction.prototype.alConstruct = function (args) {
        if (args && args.length > 0) {
            return new AVM1Point(this.context, args[0], args[1]);
        }
        else {
            return new AVM1Point(this.context, 0, 0);
        }
    };
    AVM1PointFunction.prototype.distance = function (pt1, pt2) {
        return Point.distance(toAS3Point(pt1), toAS3Point(pt2));
    };
    AVM1PointFunction.prototype.interpolate = function (pt1, pt2, f) {
        f = alToNumber(this.context, f);
        var p = Point.interpolate(toAS3Point(pt1), toAS3Point(pt2), f);
        return AVM1Point.fromAS3Point(this.context, p);
    };
    AVM1PointFunction.prototype.polar = function (len, angle) {
        len = alToNumber(this.context, len);
        angle = alToNumber(this.context, angle);
        return AVM1Point.fromAS3Point(this.context, Point.polar(len, angle));
    };
    return AVM1PointFunction;
}(AVM1Function));
export { AVM1PointFunction };
var AVM1PointPrototype = /** @class */ (function (_super) {
    __extends(AVM1PointPrototype, _super);
    function AVM1PointPrototype(context, fn) {
        var _this = _super.call(this, context) || this;
        _this.alPrototype = context.builtins.Object.alGetPrototypeProperty();
        alDefineObjectProperties(_this, {
            constructor: {
                value: fn,
                writable: true
            },
            length: {
                get: _this.getLength
            },
            add: {
                value: _this.add,
                writable: true
            },
            clone: {
                value: _this.clone,
                writable: true
            },
            equals: {
                value: _this.equals,
                writable: true
            },
            normalize: {
                value: _this.normalize,
                writable: true
            },
            offset: {
                value: _this.offset,
                writable: true
            },
            subtract: {
                value: _this.subtract,
                writable: true
            },
            toString: {
                value: _this._toString,
                writable: true
            }
        });
        return _this;
    }
    AVM1PointPrototype.prototype.getLength = function () {
        return toAS3Point(this).length;
    };
    AVM1PointPrototype.prototype.add = function (v) {
        return AVM1Point.fromAS3Point(this.context, toAS3Point(this).add(toAS3Point(v)));
    };
    AVM1PointPrototype.prototype.clone = function () {
        var result = new AVM1Point(this.context);
        if (this instanceof AVM1Object) {
            result.alPut('x', this.alGet('x'));
            result.alPut('y', this.alGet('y'));
        }
        return result;
    };
    AVM1PointPrototype.prototype.equals = function (toCompare) {
        return toAS3Point(this).equals(toAS3Point(toCompare));
    };
    AVM1PointPrototype.prototype.normalize = function (length) {
        length = alToNumber(this.context, length);
        var p = toAS3Point(this);
        p.normalize(length);
        copyAS3PointTo(p, this);
    };
    AVM1PointPrototype.prototype.offset = function (dx, dy) {
        dx = alToNumber(this.context, dx);
        dy = alToNumber(this.context, dy);
        var p = toAS3Point(this);
        p.offset(dx, dy);
        copyAS3PointTo(p, this);
    };
    AVM1PointPrototype.prototype.subtract = function (v) {
        return AVM1Point.fromAS3Point(this.context, toAS3Point(this).subtract(toAS3Point(v)));
    };
    AVM1PointPrototype.prototype._toString = function () {
        return '(x=' + this.alGet('x') + ', y=' + this.alGet('y') + ')';
    };
    return AVM1PointPrototype;
}(AVM1Object));
export { AVM1PointPrototype };
